import React, { useState } from 'react';
import Button from '../../../shared/components/Button';
import { formatDate } from '../../../shared/utils/formatDate';

const StartDate = ({ data, onNext, onBack }) => {
  const [startDate, setStartDate] = useState(data?.startDate || '');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const selectedDate = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate <= today) {
        newErrors.startDate = 'Start date must be in the future';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onNext({
        startDate
      });
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get suggested dates (next 7 days)
  const getSuggestedDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        label: formatDate(date, 'short'),
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
    }
    
    return dates;
  };

  const suggestedDates = getSuggestedDates();

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
          When should contributions start?
        </h1>
        <p className="text-neutral-600">
          Choose the date for the first contribution due
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 space-y-6">
        {/* Date Input */}
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-neutral-700 mb-2">
            First contribution due date *
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            min={getMinDate()}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.startDate ? 'border-error-500' : 'border-neutral-300'
            }`}
          />
          {errors.startDate && (
            <p className="mt-1 text-sm text-error-600">{errors.startDate}</p>
          )}
        </div>

        {/* Suggested Dates */}
        <div>
          <p className="text-sm font-medium text-neutral-700 mb-3">Quick select</p>
          <div className="grid grid-cols-2 gap-2">
            {suggestedDates.map(date => (
              <button
                key={date.value}
                type="button"
                onClick={() => setStartDate(date.value)}
                className={`p-3 text-left border rounded-lg transition-colors ${
                  startDate === date.value
                    ? 'border-primary-500 bg-primary-50 text-primary-900'
                    : 'border-neutral-300 hover:border-neutral-400 text-neutral-700'
                }`}
              >
                <div className="font-medium">{date.label}</div>
                <div className="text-xs text-neutral-500">{date.dayName}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Date Preview */}
        {startDate && !errors.startDate && (
          <div className="p-4 bg-success-50 rounded-lg">
            <h3 className="text-sm font-medium text-success-900 mb-2">First contribution</h3>
            <p className="text-sm text-success-800">
              {/* Parse date-only at LOCAL midnight — new Date('YYYY-MM-DD') is UTC and lands a day early. */}
              Will be due on: <strong>{formatDate(new Date(`${startDate}T00:00:00`), 'long')}</strong>
            </p>
            <p className="text-xs text-success-700 mt-1">
              Members will be notified 3 days before this date
            </p>
          </div>
        )}

        {/* Important Note */}
        <div className="p-4 bg-warning-50 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-warning-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-warning-900">Important</h3>
              <p className="text-xs text-warning-800 mt-1">
                Once the cycle starts, this schedule is set for all members.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex space-x-3 mt-8">
        <Button
          onClick={onBack}
          variant="secondary"
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          className="flex-1"
        >
          Create Group
        </Button>
      </div>
    </div>
  );
};

export default StartDate;
